/**
 * Clase para renderizar todo el contenido de una portada en un canvas
 * y exportarlo como imagen JPG optimizada a 300 DPI
 */
export default class CoverCanvasRenderer {
  constructor(options = {}) {
    this.dpi = options.dpi || 300
    this.jpgQuality = options.jpgQuality || 0.8 // Calidad 80% (buena calidad para impresión)
  }

  /**
   * Renderiza todo el contenido de la portada en un canvas
   * @param {Object} params - Parámetros de renderizado
   * @param {number} params.widthPx - Ancho en píxeles a 300 DPI
   * @param {number} params.heightPx - Alto en píxeles a 300 DPI
   * @param {string} params.backgroundColor - Color de fondo (hex o rgb)
   * @param {Array} params.photoSlots - Array de slots con imágenes y máscaras
   * @param {Object} params.titleConfig - Configuración del título
   * @param {Object} params.subtitleConfig - Configuración del subtítulo
   * @returns {Promise<HTMLCanvasElement>} Canvas renderizado
   */
  async renderCover(params) {
    const {
      widthPx,
      heightPx,
      backgroundColor = '#ffffff',
      photoSlots = [],
      titleConfig = null,
      subtitleConfig = null
    } = params

    // Crear canvas con dimensiones exactas a 300 DPI
    const canvas = document.createElement('canvas')
    canvas.width = widthPx
    canvas.height = heightPx
    const ctx = canvas.getContext('2d')

    // Configurar alta calidad de renderizado
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'

    // 1. Dibujar fondo
    this.drawBackground(ctx, widthPx, heightPx, backgroundColor)

    // 2. Renderizar imágenes con máscaras
    await this.renderPhotoSlots(ctx, photoSlots)

    // 3. Renderizar textos
    if (titleConfig) {
      await this.renderText(ctx, titleConfig)
    }
    if (subtitleConfig) {
      await this.renderText(ctx, subtitleConfig)
    }

    return canvas
  }

  /**
   * Dibuja el fondo del canvas
   */
  drawBackground(ctx, width, height, backgroundColor) {
    ctx.fillStyle = backgroundColor
    ctx.fillRect(0, 0, width, height)
  }

  /**
   * Renderiza las imágenes de los slots con sus máscaras
   * @param {CanvasRenderingContext2D} ctx - Contexto del canvas
   * @param {Array} photoSlots - Array de slots: [{ type: 'r'|'c', x, y, width, height, imageUrl, clipPath }]
   */
  async renderPhotoSlots(ctx, photoSlots) {
    for (const slot of photoSlots) {
      try {
        const { type, x, y, width, height, imageUrl, clipPath } = slot

        if (!imageUrl) continue

        // Cargar la imagen
        const img = await this.loadImage(imageUrl)
        if (!img) continue

        // Guardar el estado del contexto
        ctx.save()

        // Aplicar clipPath si existe
        if (clipPath) {
          this.applyClipPath(ctx, clipPath, x, y, width, height)
        } else if (type === 'c') {
          // Para círculos, crear un clipPath circular
          ctx.beginPath()
          const radius = width / 2
          ctx.arc(x + radius, y + radius, radius, 0, Math.PI * 2)
          ctx.clip()
        } else {
          // Para rectángulos, crear un clipPath rectangular
          ctx.beginPath()
          ctx.rect(x, y, width, height)
          ctx.clip()
        }

        // Dibujar la imagen con preserveAspectRatio="xMidYMid slice" (object-fit: cover)
        // Esto mantiene el aspect ratio y llena completamente el área, recortando si es necesario
        const imgWidth = img.naturalWidth || img.width
        const imgHeight = img.naturalHeight || img.height
        
        if (imgWidth && imgHeight) {
          // Calcular escala para llenar completamente el área (modo cover)
          const scale = Math.max(width / imgWidth, height / imgHeight)
          const drawWidth = imgWidth * scale
          const drawHeight = imgHeight * scale
          
          // Centrar la imagen
          const offsetX = (width - drawWidth) / 2
          const offsetY = (height - drawHeight) / 2
          
          // Dibujar la imagen escalada y centrada (el clipPath recortará el exceso)
          ctx.drawImage(img, x + offsetX, y + offsetY, drawWidth, drawHeight)
        } else {
          // Fallback: dibujar la imagen directamente si no tenemos dimensiones
          ctx.drawImage(img, x, y, width, height)
        }

        // Restaurar el estado del contexto
        ctx.restore()
      } catch (error) {
        console.error('Error rendering photo slot:', error)
      }
    }
  }

  /**
   * Aplica un clipPath SVG al contexto del canvas
   * El clipPath tiene coordenadas absolutas del SVG, necesitamos convertirlas a relativas del slot
   * @param {CanvasRenderingContext2D} ctx - Contexto del canvas
   * @param {SVGClipPathElement} clipPathElement - Elemento clipPath del SVG
   * @param {number} slotX - Posición X del slot
   * @param {number} slotY - Posición Y del slot
   * @param {number} slotWidth - Ancho del slot
   * @param {number} slotHeight - Alto del slot
   */
  applyClipPath(ctx, clipPathElement, slotX, slotY, slotWidth, slotHeight) {
    ctx.beginPath()

    // Obtener todos los elementos hijos del clipPath
    // clipPathElement.children es más confiable que childNodes para SVG
    const children = Array.from(clipPathElement.children || clipPathElement.childNodes)
    
    for (const child of children) {
      // Filtrar solo elementos (nodo tipo 1) y no nodos de texto
      if (child.nodeType !== 1) continue

      const tagName = child.tagName ? child.tagName.toLowerCase() : ''
      
      switch (tagName) {
        case 'rect':
          // El clipPath clona el rect del slot
          // Las coordenadas del clipPath son las mismas que las del slot
          // Como el canvas solo renderiza la portada (empezando en 0,0),
          // y slotX, slotY ya son relativas a la portada, usamos directamente el rectángulo del slot
          ctx.rect(slotX, slotY, slotWidth, slotHeight)
          break

        case 'circle':
          // El clipPath clona el circle del slot
          const cx = parseFloat(child.getAttribute('cx') || 0)
          const cy = parseFloat(child.getAttribute('cy') || 0)
          const r = parseFloat(child.getAttribute('r') || 0)
          
          // Convertir coordenadas absolutas a relativas del slot
          // El círculo del clipPath tiene el mismo centro que el slot
          // Calculamos el centro relativo al slot
          const relativeCx = cx - slotX
          const relativeCy = cy - slotY
          // El centro del círculo en el canvas es slotX + relativeCx, pero como el clipPath
          // es una copia del slot, el centro debería estar en el centro del slot
          const slotCenterX = slotX + (slotWidth / 2)
          const slotCenterY = slotY + (slotHeight / 2)
          ctx.arc(slotCenterX, slotCenterY, r, 0, Math.PI * 2)
          break

        case 'ellipse':
          // El clipPath clona el ellipse del slot
          const elCx = parseFloat(child.getAttribute('cx') || 0)
          const elCy = parseFloat(child.getAttribute('cy') || 0)
          const rx = parseFloat(child.getAttribute('rx') || 0)
          const ry = parseFloat(child.getAttribute('ry') || 0)
          
          // Similar al círculo, el centro debería estar en el centro del slot
          const slotElCenterX = slotX + (slotWidth / 2)
          const slotElCenterY = slotY + (slotHeight / 2)
          ctx.ellipse(slotElCenterX, slotElCenterY, rx, ry, 0, 0, Math.PI * 2)
          break

        case 'path':
          const d = child.getAttribute('d') || ''
          if (d) {
            try {
              // Para paths SVG, necesitamos aplicar una transformación
              // El path tiene coordenadas absolutas, las convertimos a relativas
              const path = new Path2D(d)
              // Aplicar transformación para mover el path a la posición correcta del slot
              ctx.save()
              ctx.translate(-slotX, -slotY) // Primero mover al origen
              ctx.translate(slotX, slotY)   // Luego mover a la posición del slot
              ctx.addPath(path)
              ctx.restore()
            } catch (error) {
              console.warn('Error parsing SVG path in clipPath:', error)
              // Fallback: usar el rectángulo del slot
              ctx.rect(slotX, slotY, slotWidth, slotHeight)
            }
          }
          break

        default:
          // Si hay otros elementos, usar el rectángulo del slot como fallback
          console.warn('Unsupported clipPath element:', tagName)
          ctx.rect(slotX, slotY, slotWidth, slotHeight)
          break
      }
    }

    ctx.clip()
  }

  /**
   * Renderiza un texto (título o subtítulo) en el canvas
   * @param {CanvasRenderingContext2D} ctx - Contexto del canvas
   * @param {Object} textConfig - Configuración del texto
   */
  async renderText(ctx, textConfig) {
    const {
      text,
      x,
      y,
      fontSize,
      fontFamily,
      fontWeight = 'normal',
      color = '#000000',
      textAlign = 'center',
      textBaseline = 'middle',
      lines = null // Si hay múltiples líneas
    } = textConfig

    // Esperar a que las fuentes se carguen
    await document.fonts.ready
    try {
      await document.fonts.load(`${fontWeight} ${fontSize}px "${fontFamily}"`)
      await new Promise(resolve => setTimeout(resolve, 100))
    } catch (e) {
      console.warn('No se pudo cargar la fuente:', fontFamily, e)
    }

    ctx.save()

    // Configurar estilo de texto
    ctx.font = `${fontWeight} ${fontSize}px "${fontFamily}", sans-serif`
    ctx.fillStyle = color
    ctx.textAlign = textAlign === 'start' ? 'left' : textAlign === 'end' ? 'right' : 'center'
    ctx.textBaseline = textBaseline === 'hanging' ? 'top' : textBaseline === 'middle' ? 'middle' : 'bottom'

    // Renderizar texto (soporta múltiples líneas)
    if (lines && Array.isArray(lines)) {
      const lineHeight = fontSize * 1.2
      const startY = y - ((lines.length - 1) * lineHeight) / 2

      lines.forEach((line, index) => {
        const lineY = startY + (index * lineHeight)
        ctx.fillText(line, x, lineY)
      })
    } else {
      ctx.fillText(text || '', x, y)
    }

    ctx.restore()
  }

  /**
   * Carga una imagen desde una URL
   * @param {string} imageUrl - URL de la imagen (blob, data URL, o URL normal)
   * @returns {Promise<HTMLImageElement>}
   */
  loadImage(imageUrl) {
    return new Promise((resolve, reject) => {
      // Si ya es un data URL o blob URL, cargarlo directamente
      const img = new Image()
      img.crossOrigin = 'anonymous'

      img.onload = () => resolve(img)
      img.onerror = (error) => {
        console.error('Error loading image:', imageUrl, error)
        reject(error)
      }

      img.src = imageUrl
    })
  }

  /**
   * Exporta el canvas como JPG con calidad especificada
   * @param {HTMLCanvasElement} canvas - Canvas a exportar
   * @returns {string} Data URL de la imagen JPG
   */
  exportAsJPG(canvas) {
    return canvas.toDataURL('image/jpeg', this.jpgQuality)
  }

  /**
   * Método principal que renderiza y exporta la portada completa
   * @param {Object} params - Parámetros de renderizado (igual que renderCover)
   * @returns {Promise<string>} Data URL de la imagen JPG
   */
  async renderAndExport(params) {
    const canvas = await this.renderCover(params)
    return this.exportAsJPG(canvas)
  }

  /**
   * Extrae información de clipPath de un elemento SVG
   * @param {SVGElement} svgElement - Elemento SVG que contiene el clipPath
   * @param {string} clipPathId - ID del clipPath (puede ser 'url(#id)' o solo 'id')
   * @returns {SVGClipPathElement|null}
   */
  getClipPathElement(svgElement, clipPathId) {
    if (!svgElement || !clipPathId) return null

    // Remover 'url(' y ')' si existen
    let cleanId = clipPathId.trim()
    if (cleanId.startsWith('url(')) {
      cleanId = cleanId.replace(/^url\(#?/, '').replace(/\)$/, '')
    }
    if (cleanId.startsWith('#')) {
      cleanId = cleanId.substring(1)
    }
    
    // Buscar en el SVG o en el documento completo
    const clipPath = svgElement.querySelector(`#${cleanId}`) || 
                     svgElement.ownerDocument?.getElementById(cleanId) ||
                     document.getElementById(cleanId)
    
    return clipPath
  }
}
